/**
 * Credit PD (`creditPd.hazardBurnout@1`, P4-D13 stock rule): per book, a trend loss flow through the charge-off queue and a stress loss
 * that is a working-out stock on the worst decline of the book's macro driver (04.1), with nonaccrual on `BankState.credit[book]` (04.2).
 *   04.1 E1  D_b(m)  driver level on the lagged env, against the SimStart env (unemployment, hpi, CRE index with the 10y cut)
 *   04.1 E2  d_b = max(d_b, D_b)                               worst decline so far
 *   04.1 E3  τ(d) = Σ_s share_s·[a_s·d + S_s·(max(0, d − h_s)/(d_sev − h_s))^k]   lifetime stress loss per $ (cushion h, power k)
 *   04.1 E4  ΔT_v = B_v·π_v·(τ(d_b) − τ(d_b⁻))                stress loss recognized this month (provisioned at once, 04.3)
 *   04.1 E5  π_v = [1 + (M_v − 1)·σ_p(a_v)]·k_v·LGD_v/LGD_b, σ_p(a) = r(a)/r̄_p the box seasoning (consumer, card; the 03.4 E3 ramp)
 *   04.1 E6  ℓ_v = B_v·π_v·λ0_b/12 + ΔT_v                       loss-at-default (trend) plus the recognized stress loss
 *   04.1 E7  d_v = B_v·π_v·λ0_b/(12·LGD_v)                      defaulted balance of the trend flow
 *   04.2 E1  NCO_b = Σ_i w_i·ℓ^tr_b(m − 3i) + W_b + CO^ev_b    queue slot 3i receives w_i of the trend loss; W_b the workout
 *   04.2 E3  NA_b = n0_b·B_b + Σ_j [d^x(m−j) − ℓ^x(m−j)·W(j)] + N_b + NA^ev_b
 *   04.2 E4  B_b ← B_b − trim + ΔT_b, W_b = ω·B_b, B_b −= W_b   workout backlog, ω = workoutRatePerMonth
 *   04.2 E5  trim = θ·B_b·(R_b − D_b)/d_b⁻ when D_b < R_b       a new low since the worst trims the open backlog (≤ θ in all)
 *   04.2 E6  N_b ← N_b·(1 − x) + e·B_b                          stress nonaccrual: entry e of the backlog, exit x a month
 * Money rounds once per book-month; the per-vintage loss and the schedule split use `splitUsdCumulative`.
 * Charge-offs of the vintages and closing balances belong to the engine (03.2).
 */
import {
  InvariantViolation, roundToUsd, splitUsdCumulative,
  type BookCreditState, type BookId, type CreditMults, type CreditPdModel, type CreditPdParams, type EnvSnapshot,
  type ModelKind, type ModelRefOf, type ProductDef, type Rate, type Usd, type Vintage,
} from "@bsim/domain";

const NEUTRAL: CreditMults = { lossMult: 1, creSeverityAddRate: 0 as Rate, shockPdMult: 1 };

/** The book's ModelRef of `kind`, from its products (every product of a book carries the same creditPd and cecl refs). */
export function bookModelRef<K extends ModelKind>(products: readonly ProductDef[], bookId: BookId, kind: K): ModelRefOf<K> {
  for (const p of products) {
    if (p.bookId !== bookId) continue;
    const ref = p.models.find((m): m is ModelRefOf<K> => m.kind === kind);
    if (ref) return ref;
  }
  throw new InvariantViolation("I19", `no ${kind} model ref for book ${bookId}`);
}

const productsById = new WeakMap<readonly ProductDef[], ReadonlyMap<string, ProductDef>>();

export function productOf(products: readonly ProductDef[], productId: string): ProductDef {
  let byId = productsById.get(products);
  if (byId === undefined) productsById.set(products, (byId = new Map(products.map((p) => [p.id as string, p]))));
  const product = byId.get(productId);
  if (!product) throw new InvariantViolation("I19", `unknown product ${productId}`);
  return product;
}

/** 04.1 E1: the level D_b of the book's driver (the `macroBetas` key names it) on `env` relative to the SimStart env `base`. */
export function driverLevel(p: CreditPdParams, env: EnvSnapshot, base: EnvSnapshot): number {
  const { unemployment, hpi, creIndex } = p.macroBetas;
  if (unemployment !== undefined) return Math.max(0, (env.unemploymentRate - base.unemploymentRate) * 100);
  if (hpi !== undefined) return Math.max(0, 1 - env.hpiIndex / base.hpiIndex);
  if (creIndex !== undefined) {
    const cut = (p.longRateIndexCutPerPp ?? 0) * Math.max(0, (env.curveRate["10y"] - base.curveRate["10y"]) * 100);
    return Math.max(0, 1 - (env.creIndex * (1 - cut)) / base.creIndex);
  }
  throw new InvariantViolation("I19", "creditPd macroBetas has no supported driver");
}

/** 04.1 E3 τ(d): the lifetime stress loss per $ (box-weighted) at worst decline d; 0 without a loss curve. */
export function lifetimeExcessRate(p: CreditPdParams, worst: number): number {
  const curve = p.lossCurve;
  if (curve === undefined) return 0;
  let rate = 0;
  for (const s of curve.segments) {
    rate += s.shareRate * (s.preCushionSlopeRate * worst + s.severeExcessLossRate * Math.max(0, (worst - s.driverCushion) / (curve.severeDriverLevel - s.driverCushion)) ** curve.excessPower);
  }
  return rate;
}

/**
 * The book's annual default rate if the driver moved to its level at `env` from SimStart: the base PD plus the stress loss that the first year's
 * workout charges off (04.1 E3, 04.2 E4), as defaulted balance (÷ LGD). The Market Outlook's default-rate series.
 */
export function defaultAnnualRate(p: CreditPdParams, env: EnvSnapshot, baselineEnv: EnvSnapshot): number {
  const firstYear = p.lossCurve === undefined ? 0 : 1 - (1 - p.lossCurve.workoutRatePerMonth) ** 12;
  return p.basePdAnnualRate + (lifetimeExcessRate(p, driverLevel(p, env, baselineEnv)) * firstYear) / p.lgdBaseRate;
}

interface Seasoning { readonly f0: number; readonly ramp: number; readonly mean: number }
const seasoningByProduct = new WeakMap<readonly ProductDef[], Map<string, Seasoning>>();

/** 04.1 E4 r̄_p: the recognition ramp averaged over the new-vintage runoff profile (geometric to 120 months, else flat to term). */
function rampMean(product: ProductDef, f0: number, rampMonths: number): number {
  const runoff = product.runoffRatePerMonth;
  const months = runoff !== undefined ? 120 : product.termMonths;
  if (months === undefined) throw new InvariantViolation("I19", `product ${product.id} has neither runoff nor term for seasoning`);
  let weighted = 0;
  let total = 0;
  let survival = 1;
  for (let a = 1; a <= months; a++) {
    if (runoff !== undefined) survival *= 1 - runoff;
    weighted += (f0 + (1 - f0) * Math.min(1, a / rampMonths)) * survival;
    total += survival;
  }
  return weighted / total;
}

/** The product's 04.1 E4 ramp and r̄_p, found once per product list. */
function seasoningOf(products: readonly ProductDef[], productId: string): Seasoning {
  let cache = seasoningByProduct.get(products);
  if (cache === undefined) seasoningByProduct.set(products, (cache = new Map()));
  let s = cache.get(productId);
  if (s === undefined) {
    const product = productOf(products, productId);
    const { recognitionStartFraction: f0, recognitionRampMonths: ramp } = bookModelRef(products, product.bookId, "underwriting").params;
    cache.set(productId, (s = { f0, ramp, mean: rampMean(product, f0, ramp) }));
  }
  return s;
}

/** 04.1 E4: σ_p(a) for a product's seasoning at `ageMonths`. */
const sigmaAt = (s: Seasoning, ageMonths: number): number => (s.f0 + (1 - s.f0) * Math.min(1, ageMonths / s.ramp)) / s.mean;

/** 04.2 E3 W(j): share of a default cohort charged off within j months (schedule quarter i posts at month 3i). */
const chargedOffShare = (schedule: readonly number[], months: number): number => {
  let share = 0;
  for (let i = 0; i < schedule.length && 3 * i <= months; i++) share += schedule[i] ?? 0;
  return share;
};

/** Event sleeves after this month's due charge-offs (04.4 E2): total charged off, and the sleeves still open. */
function payEventSleeves(sleeves: BookCreditState["eventSleeves"], month: number): { chargedOffUsd: number; open: BookCreditState["eventSleeves"] } {
  let chargedOffUsd = 0;
  const open: BookCreditState["eventSleeves"][number][] = [];
  for (const s of sleeves) {
    const due = s.chargeOffsDue.filter((d) => d.month === month).reduce((a, d) => a + d.usd, 0);
    chargedOffUsd += due;
    const chargeOffsDue = s.chargeOffsDue.filter((d) => d.month !== month);
    const reserveUsd = (s.reserveUsd - due) as Usd;
    if (chargeOffsDue.length > 0 || reserveUsd !== 0) open.push({ ...s, reserveUsd, nonaccrualUsd: Math.max(0, s.nonaccrualUsd - due) as Usd, chargeOffsDue });
  }
  return { chargedOffUsd, open };
}

/** Whether a per-vintage record holds any entry: only non-neutral vintages have one, so an empty record needs no per-vintage lookup. */
export function hasKeys(record: object): boolean {
  // eslint-disable-next-line no-restricted-syntax -- emptiness only: the first key ends the loop, no order is read (D1)
  for (const _ in record) return true;
  return false;
}

const NO_VINTAGES: Record<string, Usd> = Object.freeze({});

/**
 * `splitUsdCumulative` (README §4) in place: `w[0..n)` are the weights (summing to `sumW`), on return the parts of `total`.
 * The same cumulative rounding, so the parts match the allocating helper exactly.
 */
function splitInPlace(total: number, w: Float64Array, n: number, sumW: number): void {
  if (total === 0) { w.fill(0, 0, n); return; }
  if (!(sumW > 0)) throw new InvariantViolation("I19", "creditPd: bad loss weights");
  let cum = 0;
  let prev = 0;
  for (let i = 0; i < n; i++) {
    const wi = w[i] as number;
    if (!(wi >= 0)) throw new InvariantViolation("I19", "creditPd: bad loss weights");
    cum += wi;
    const at = roundToUsd((total * cum) / sumW);
    w[i] = roundToUsd(at - prev);
    prev = at;
  }
}

const shift = <T>(window: readonly T[], head: T, length: number): T[] => [head, ...window.slice(0, Math.max(0, length - 1))];

export const creditPdHazardBurnout: CreditPdModel = {
  kind: "creditPd",
  monthly: (call, input) => {
    const p = call.ref.params;
    const curve = p.lossCurve;
    const { vintages, products, multByVintage, driverEnv, state } = input;
    const trendRate = p.basePdAnnualRate * p.lgdBaseRate; // λ0_b
    const level = driverLevel(p, driverEnv, call.baselineEnv); // E1
    const worstBefore = state.worstDriverLevel ?? 0;
    const markBefore = state.recoveryDriverLevel ?? 0;
    const backlogBefore = state.workoutBacklogUsd ?? 0;
    const worst = Math.max(worstBefore, level); // E2
    const recognizedRate = lifetimeExcessRate(p, worst) - lifetimeExcessRate(p, worstBefore); // E3–E4

    const n = vintages.length;
    const loss = input.intoUsd ?? new Float64Array(n);
    let openingUsd = 0;
    let defaults = 0;
    let trendLoss = 0;
    let totalLoss = 0;
    const anyMult = hasKeys(multByVintage); // only non-neutral vintages have a record: with none, no per-vintage lookup
    let seasoned: Seasoning | undefined; // vintages of one product sit together: the last product's ramp is reused
    let seasonedId = "";
    for (let i = 0; i < n; i++) {
      const v = vintages[i] as Vintage;
      const m = anyMult ? multByVintage[v.id] ?? NEUTRAL : NEUTRAL;
      const balanceUsd = v.balanceUsd;
      const lgd = (m.shockLgdRate ?? p.lgdBaseRate) + m.creSeverityAddRate;
      let sigma = 1;
      if (p.seasonsBoxEffect) {
        if (seasoned === undefined || v.productId !== seasonedId) { seasonedId = v.productId; seasoned = seasoningOf(products, seasonedId); }
        sigma = sigmaAt(seasoned, call.month - v.originMonth + (v.ageAtSimStartMonths ?? 0));
      }
      const weighted = balanceUsd * (1 + (m.lossMult - 1) * sigma) * m.shockPdMult * (lgd / p.lgdBaseRate); // B_v·π_v (E5)
      const trend = (weighted * trendRate) / 12;
      const l = trend + weighted * recognizedRate; // E6
      loss[i] = l;
      trendLoss += trend;
      totalLoss += l;
      defaults += trend / lgd; // E7
      openingUsd += balanceUsd;
    }
    const lossUsd = roundToUsd(totalLoss);
    const trendUsd = roundToUsd(trendLoss);
    splitInPlace(lossUsd, loss, n, totalLoss);

    // 04.2 E1: this month's slot is 1 (slot k of last month's queue is due k months on); slot 3i takes w_i of the trend loss. The new queue's
    // slot 0 is due this month and was just charged off, so it stays empty (as at SimStart) and the ACL (04.3 E3) never reserves it.
    const schedule = p.chargeOffScheduleByQuarter;
    const shares = splitUsdCumulative(trendUsd, schedule);
    const queue: number[] = Array.from({ length: 3 * (schedule.length - 1) + 1 }, (_, k) => (k === 0 ? 0 : state.chargeOffQueueUsd[k + 1] ?? 0));
    for (let i = 1; i < schedule.length; i++) queue[3 * i] = (queue[3 * i] ?? 0) + (shares[i] ?? 0);
    const sleeves = payEventSleeves(state.eventSleeves, call.month);

    // 04.2 E4–E5: a new low since the worst trims the open backlog; the recognized loss joins it; a fixed share works out each month.
    const trimUsd = curve !== undefined && level < markBefore ? roundToUsd((curve.recoveryTrimMaxRate * backlogBefore * (markBefore - level)) / worstBefore) : 0;
    const backlogUsd = backlogBefore - trimUsd + (lossUsd - trendUsd);
    const workoutUsd = curve === undefined ? 0 : roundToUsd(curve.workoutRatePerMonth * backlogUsd);
    const chargeOffUsd = (shares[0] ?? 0) + (state.chargeOffQueueUsd[1] ?? 0) + workoutUsd + sleeves.chargedOffUsd;

    // 04.2 E3, E6: only above-trend defaults use the explicit pipeline, plus the backlog's nonaccrual; card (0 quarters) is never nonaccrual.
    const windowLength = 3 * p.nonaccrualQuarters;
    const trendBaseLoss = (openingUsd * trendRate) / 12;
    const aboveTrendLossUsd = shift(state.aboveTrendLossUsd, roundToUsd(Math.max(0, trendLoss - trendBaseLoss)), windowLength);
    const aboveTrendDefaultsUsd = shift(state.aboveTrendDefaultsUsd, roundToUsd(Math.max(0, defaults - trendBaseLoss / p.lgdBaseRate)), windowLength);
    let nonaccrualUsd = 0 as Usd;
    let stressNonaccrualUsd = 0;
    if (windowLength > 0) {
      if (curve !== undefined) {
        stressNonaccrualUsd = roundToUsd((state.stressNonaccrualUsd ?? 0) * (1 - curve.nonaccrualExitRatePerMonth) + curve.nonaccrualEntryRatePerMonth * (backlogUsd - workoutUsd));
      }
      const closingUsd = input.closingBalanceUsd ?? openingUsd - chargeOffUsd;
      let pipeline = 0;
      for (let j = 0; j < windowLength; j++) pipeline += (aboveTrendDefaultsUsd[j] ?? 0) - (aboveTrendLossUsd[j] ?? 0) * chargedOffShare(schedule, j);
      nonaccrualUsd = roundToUsd((p.trendNonaccrualRate ?? 0) * closingUsd + pipeline + stressNonaccrualUsd + sleeves.open.reduce((a, s) => a + s.nonaccrualUsd, 0));
    }

    let byVintage = NO_VINTAGES;
    if (input.intoUsd === undefined) {
      byVintage = {};
      for (let i = 0; i < n; i++) byVintage[(vintages[i] as Vintage).id] = loss[i] as Usd;
    }
    return {
      lossAtDefaultByVintageUsd: byVintage,
      defaultsUsd: roundToUsd(defaults),
      chargeOffUsd: chargeOffUsd as Usd,
      nonaccrualUsd,
      nextState: {
        ...state,
        chargeOffQueueUsd: queue as Usd[],
        aboveTrendDefaultsUsd,
        aboveTrendLossUsd,
        nonaccrualUsd,
        eventSleeves: sleeves.open,
        worstDriverLevel: worst,
        recoveryDriverLevel: level >= worstBefore ? level : Math.min(markBefore, level),
        workoutBacklogUsd: (backlogUsd - workoutUsd) as Usd,
        stressNonaccrualUsd: stressNonaccrualUsd as Usd,
      },
    };
  },
};
